/**
 * FreeToolPage
 * Public, share-friendly landing page for the 3 free tools:
 *   /free/cost-calculator   -> Cost Calculator
 *   /free/deal-analyzer     -> Deal Analyzer
 *   /free/negotiation-coach -> Negotiation Coach
 *
 * Flow:
 *   1. Visitor lands on the page → sees a polished hero + email gate.
 *   2. Enters email + clicks Unlock → POST /api/free-tools/unlock
 *      - if the email is brand-new, a lead is created with this tool unlocked
 *      - if the email already exists with this tool unlocked, we treat it as
 *        a returning user and redirect to /my-quiz (My Profile) so they see
 *        every tool they have access to (free or paid).
 *   3. Successful unlock for first-time-this-tool users renders the actual
 *      tool component inline.
 *
 * Once unlocked, the user's email is cached in localStorage so a refresh
 * doesn't re-prompt.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Calculator, FileSearch, MessageSquare, ArrowRight, CheckCircle2, Lock, Mail, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useTheme } from '../context/ThemeContext';
import { CostCalculatorContent } from './CostCalculatorPage';
import { DealAnalyzerContent } from './DealAnalyzerPage';
import NegotiationCoachPage from './NegotiationCoachPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tool registry — slug → metadata + component renderer
const TOOLS = {
  'cost-calculator': {
    slug: 'cost_calculator',
    name: 'RV Cost Calculator',
    tagline: 'See the true 5-year cost of any RV in 60 seconds',
    description: 'Most buyers underestimate RV ownership by $20K+. This calculator factors in financing, depreciation, fuel, insurance, storage, and maintenance to show you the full picture.',
    Icon: Calculator,
    accent: '#22c55e',
    bullets: [
      'Total cost projections out to 5 years',
      'Year-by-year ownership breakdown',
      'Fuel + insurance + maintenance + storage',
      'Financing scenarios with interest math',
    ],
    Component: CostCalculatorContent,
  },
  'deal-analyzer': {
    slug: 'deal_analyzer',
    name: 'RV Deal Analyzer',
    tagline: 'Is this RV listing actually a good deal?',
    description: 'Paste a listing price and our AI compares against market data, condition adjustments, and similar units to tell you if it\'s a steal, fair, or overpriced — with a counter-offer suggestion.',
    Icon: FileSearch,
    accent: '#3b82f6',
    bullets: [
      'AI-powered fair-value range',
      'Compare against similar units',
      'Counter-offer suggestion you can use today',
      'Catches dealer markup tricks',
    ],
    Component: DealAnalyzerContent,
  },
  'negotiation-coach': {
    slug: 'negotiation_coach',
    name: 'RV Negotiation Coach',
    tagline: 'The exact script to use when you walk into the dealership',
    description: 'AI-generated negotiation playbook tailored to the specific RV you\'re looking at — including the opening line, the exact dollar amount to counter with, and what to say when they push back.',
    Icon: MessageSquare,
    accent: '#a855f7',
    bullets: [
      'Word-for-word opening script',
      'Counter-offer math + justification',
      'Comeback lines for common dealer pushback',
      'Walk-away threshold (so you don\'t overpay)',
    ],
    Component: NegotiationCoachPage,
  },
};

const FreeToolPage = () => {
  const { slug: routeSlug } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const tool = TOOLS[routeSlug];

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  // Auto-unlock if same email already cached locally
  useEffect(() => {
    if (!tool) return;
    const cachedEmail = localStorage.getItem('rv_free_tool_email');
    const cachedTools = JSON.parse(localStorage.getItem('rv_free_tool_unlocked') || '[]');
    if (cachedEmail && cachedTools.includes(tool.slug)) {
      setEmail(cachedEmail);
      setUnlocked(true);
    }
  }, [tool]);

  if (!tool) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Tool not found</h1>
          <p className="mb-4 text-gray-500">The link you followed doesn't lead anywhere.</p>
          <Link to="/" className="text-[#004E86] underline font-semibold">Go home</Link>
        </div>
      </div>
    );
  }

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/free-tools/unlock`, {
        email: email.trim().toLowerCase(),
        tool_slug: tool.slug,
        name: name.trim() || null,
        source_link: `${tool.slug}_share`,
      });
      const data = res.data;
      // Cache locally so a refresh doesn't re-prompt
      localStorage.setItem('rv_free_tool_email', data.email);
      localStorage.setItem('rv_free_tool_unlocked', JSON.stringify(data.unlocked_tools || []));

      if (data.is_returning_user && (data.unlocked_tools || []).length > 1) {
        // Returning user with multiple tools — send them to their profile
        // so they see everything they own.
        toast.success('Welcome back! Loading your profile…');
        navigate(`/my-quiz?email=${encodeURIComponent(data.email)}`);
        return;
      }

      toast.success(`${tool.name} unlocked!`);
      setUnlocked(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock tool');
    } finally {
      setLoading(false);
    }
  };

  if (unlocked) {
    const ToolComponent = tool.Component;
    return (
      <div className={isDark ? 'bg-gray-900' : 'bg-white'}>
        {/* Slim banner reminder */}
        <div
          className="text-center py-2 px-4 text-xs sm:text-sm text-white"
          style={{ background: tool.accent }}
          data-testid="free-tool-banner"
        >
          <span className="opacity-90">You're using <span className="font-bold">{tool.name}</span> for free · </span>
          <Link to="/my-quiz" className="underline font-bold hover:opacity-90">View My Profile</Link>
        </div>
        <ToolComponent />
      </div>
    );
  }

  // Email gate UI
  const Icon = tool.Icon;
  return (
    <div
      className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-gray-50 to-white'}`}
      data-testid={`free-tool-${routeSlug}`}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left: Pitch */}
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-4"
              style={{ background: `${tool.accent}20`, color: tool.accent }}
            >
              <Sparkles size={12} />
              FREE TOOL — NO CREDIT CARD
            </div>
            <h1
              className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}
              data-testid="free-tool-title"
            >
              {tool.name}
            </h1>
            <p className={`text-lg sm:text-xl mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {tool.tagline}
            </p>
            <p className={`text-base mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {tool.description}
            </p>
            <ul className="space-y-2 mb-8">
              {tool.bullets.map((b, i) => (
                <li key={i} className={`flex items-start gap-2 text-sm sm:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <CheckCircle2 size={18} style={{ color: tool.accent }} className="mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Email gate card */}
          <div
            className={`rounded-2xl p-6 sm:p-8 shadow-2xl ${isDark ? 'bg-gray-800 ring-1 ring-gray-700' : 'bg-white ring-1 ring-gray-200'}`}
            data-testid="email-gate"
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `${tool.accent}20`, color: tool.accent }}
              >
                <Icon size={22} />
              </div>
              <div>
                <div className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Get instant access</div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Just your email — that's it.</div>
              </div>
            </div>

            <form onSubmit={handleUnlock} className="space-y-3">
              <div>
                <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  First Name <span className="opacity-60 font-normal normal-case">(optional)</span>
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sara"
                  className={`mt-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                  data-testid="free-tool-name-input"
                />
              </div>
              <div>
                <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Email <span style={{ color: tool.accent }}>*</span>
                </label>
                <div className="relative mt-1">
                  <Mail
                    size={16}
                    className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                  />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className={`pl-9 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                    data-testid="free-tool-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full font-bold py-3 text-base"
                style={{ background: tool.accent, color: 'white' }}
                data-testid="free-tool-unlock-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Unlocking…
                  </>
                ) : (
                  <>
                    <Lock size={16} className="mr-2" />
                    Unlock {tool.name}
                  </>
                )}
              </Button>
            </form>

            <div className={`mt-4 pt-4 border-t text-center ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Already used a tool before?{' '}
                <Link to="/my-quiz" className="font-semibold" style={{ color: tool.accent }} data-testid="my-profile-link">
                  Open My Profile <ArrowRight size={12} className="inline" />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeToolPage;
